/**
 * Documents List PDF Layout
 *
 * Creates a summary layout for travel documents and files.
 * Shows document names, types, descriptions with organization summary.
 */

export class DocumentsListPDFLayout {
  constructor() {
    this.layoutType = 'table';
    this.name = 'Documents List Layout';
  }

  /**
   * Generate PDF content structure for documents list
   * @param {Object} pageContext - Page context with documents data
   * @returns {Object} PDF content structure
   */
  async generate(pageContext) {
    const documentsData = Array.isArray(pageContext.data) ? pageContext.data : [];

    if (documentsData.length === 0) {
      return this.generateEmptyContent(pageContext);
    }

    // Group documents by type and category
    const groupedDocuments = this.groupDocumentsByType(documentsData);

    return {
      sections: [
        this.generateSummarySection(documentsData, pageContext),
        this.generateDocumentListSection(documentsData, pageContext),
        ...this.generateDocumentTypeGroups(groupedDocuments, pageContext),
        this.generateAssociationsSection(documentsData, pageContext)
      ].filter(section => section !== null)
    };
  }

  /**
   * Generate summary section with documents overview
   * @param {Array} documentsData - Documents data
   * @param {Object} pageContext - Page context
   * @returns {Object} Summary section
   */
  generateSummarySection(documentsData, pageContext) {
    const stats = this.calculateDocumentsStats(documentsData);

    return {
      type: 'table',
      title: 'Documents Overview',
      columns: ['Category', 'Count'],
      rows: [
        ['Total Documents', stats.total.toString()],
        ['Travel Documents', stats.travel.toString()],
        ['Accommodation', stats.accommodation.toString()],
        ['Transportation', stats.transportation.toString()],
        ['Insurance & Legal', stats.insurance.toString()],
        ['Photos & Media', stats.media.toString()],
        ['Other', stats.other.toString()]
      ]
    };
  }

  /**
   * Generate main documents list section
   * @param {Array} documentsData - Documents data
   * @param {Object} pageContext - Page context
   * @returns {Object} Documents list section
   */
  generateDocumentListSection(documentsData, pageContext) {
    // Sort documents by upload date (most recent first)
    const sortedDocuments = this.sortDocumentsByDate(documentsData);

    return {
      type: 'table',
      title: 'All Documents',
      columns: ['Name', 'Type', 'Category', 'Upload Date', 'Size', 'Description'],
      rows: sortedDocuments.map(doc => [
        doc.name || doc.filename || 'Unnamed Document',
        this.formatDocumentType(doc),
        this.categorizeDocument(doc),
        this.formatUploadDate(doc),
        this.formatFileSize(doc),
        this.formatDescription(doc)
      ])
    };
  }

  /**
   * Generate document type groups
   * @param {Object} groupedDocuments - Documents grouped by type
   * @param {Object} pageContext - Page context
   * @returns {Array} Document type sections
   */
  generateDocumentTypeGroups(groupedDocuments, pageContext) {
    const sections = [];

    Object.entries(groupedDocuments).forEach(([category, documents]) => {
      if (documents.length > 0) {
        sections.push({
          type: 'table',
          title: this.formatCategoryTitle(category),
          columns: ['Document Name', 'Type', 'Upload Date', 'Notes'],
          rows: documents.map(doc => [
            doc.name || doc.filename || 'Unnamed Document',
            this.formatDocumentType(doc),
            this.formatUploadDate(doc),
            doc.notes || doc.description || '-'
          ])
        });
      }
    });

    return sections;
  }

  /**
   * Generate associations section showing document relationships
   * @param {Array} documentsData - Documents data
   * @param {Object} pageContext - Page context
   * @returns {Object|null} Associations section or null
   */
  generateAssociationsSection(documentsData, pageContext) {
    const documentsWithAssociations = documentsData.filter(doc =>
      (doc.associatedDays && doc.associatedDays.length > 0) ||
      (doc.associatedPlaces && doc.associatedPlaces.length > 0) ||
      (doc.tags && doc.tags.length > 0)
    );

    if (documentsWithAssociations.length === 0) {
      return null;
    }

    return {
      type: 'table',
      title: 'Document Associations',
      columns: ['Document', 'Associated Days', 'Associated Places', 'Tags'],
      rows: documentsWithAssociations.map(doc => [
        doc.name || doc.filename || 'Unnamed Document',
        this.formatAssociations(doc.associatedDays),
        this.formatAssociations(doc.associatedPlaces),
        this.formatTags(doc.tags)
      ])
    };
  }

  /**
   * Generate content for empty documents list
   * @param {Object} pageContext - Page context
   * @returns {Object} Empty content structure
   */
  generateEmptyContent(pageContext) {
    return {
      sections: [{
        type: 'text',
        title: 'No Documents Uploaded',
        content: 'This trip does not have any documents uploaded yet. Add some travel documents, photos, or other files to generate a more detailed PDF export.'
      }]
    };
  }

  /**
   * Group documents by category
   * @param {Array} documentsData - Documents data
   * @returns {Object} Documents grouped by category
   */
  groupDocumentsByType(documentsData) {
    const groups = {
      travel: [],
      accommodation: [],
      transportation: [],
      insurance: [],
      media: [],
      other: []
    };

    documentsData.forEach(doc => {
      const category = this.categorizeDocument(doc).toLowerCase().replace(/\s+/g, '');
      const groupKey = this.mapCategoryToGroup(category);

      if (groups[groupKey]) {
        groups[groupKey].push(doc);
      } else {
        groups.other.push(doc);
      }
    });

    // Sort within each group by name
    Object.keys(groups).forEach(category => {
      groups[category].sort((a, b) => {
        const nameA = (a.name || a.filename || '').toLowerCase();
        const nameB = (b.name || b.filename || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
    });

    return groups;
  }

  /**
   * Map category to group key
   * @param {string} category - Document category
   * @returns {string} Group key
   */
  mapCategoryToGroup(category) {
    if (category.includes('travel') || category.includes('passport') || category.includes('visa')) {
      return 'travel';
    }
    if (category.includes('accommodation') || category.includes('hotel')) {
      return 'accommodation';
    }
    if (category.includes('transportation') || category.includes('flight') || category.includes('train')) {
      return 'transportation';
    }
    if (category.includes('insurance') || category.includes('legal')) {
      return 'insurance';
    }
    if (category.includes('media') || category.includes('photos')) {
      return 'media';
    }
    return 'other';
  }

  /**
   * Categorize a document
   * @param {Object} doc - Document object
   * @returns {string} Document category
   */
  categorizeDocument(doc) {
    // Use explicit category if available
    if (doc.category) {
      return doc.category;
    }

    // Categorize based on type or name
    const type = (doc.type || doc.mimeType || '').toLowerCase();
    const name = (doc.name || doc.filename || '').toLowerCase();

    if (type.includes('image') || name.includes('photo') || name.includes('picture')) {
      return 'Photos & Media';
    }

    if (name.includes('passport') || name.includes('visa') || name.includes('id')) {
      return 'Travel Documents';
    }

    if (name.includes('hotel') || name.includes('booking') || name.includes('reservation')) {
      return 'Accommodation';
    }

    if (name.includes('flight') || name.includes('ticket') || name.includes('boarding')) {
      return 'Transportation';
    }

    if (name.includes('insurance') || name.includes('policy') || name.includes('legal')) {
      return 'Insurance & Legal';
    }

    return 'Other';
  }

  /**
   * Sort documents by upload date
   * @param {Array} documentsData - Documents data
   * @returns {Array} Sorted documents
   */
  sortDocumentsByDate(documentsData) {
    return documentsData
      .sort((a, b) => {
        const dateA = new Date(a.uploadDate || a.createdAt || 0);
        const dateB = new Date(b.uploadDate || b.createdAt || 0);
        return dateB - dateA; // Most recent first
      });
  }

  /**
   * Calculate documents statistics
   * @param {Array} documentsData - Documents data
   * @returns {Object} Statistics object
   */
  calculateDocumentsStats(documentsData) {
    const stats = {
      total: documentsData.length,
      travel: 0,
      accommodation: 0,
      transportation: 0,
      insurance: 0,
      media: 0,
      other: 0
    };

    documentsData.forEach(doc => {
      const category = this.categorizeDocument(doc).toLowerCase();

      if (category.includes('travel')) {
        stats.travel++;
      } else if (category.includes('accommodation')) {
        stats.accommodation++;
      } else if (category.includes('transportation')) {
        stats.transportation++;
      } else if (category.includes('insurance')) {
        stats.insurance++;
      } else if (category.includes('media')) {
        stats.media++;
      } else {
        stats.other++;
      }
    });

    return stats;
  }

  /**
   * Format document type
   * @param {Object} doc - Document object
   * @returns {string} Formatted type
   */
  formatDocumentType(doc) {
    if (doc.type) {
      return doc.type;
    }

    if (doc.mimeType) {
      // Convert mime type to readable format
      const mimeType = doc.mimeType.toLowerCase();
      if (mimeType.includes('pdf')) return 'PDF';
      if (mimeType.includes('image')) return 'Image';
      if (mimeType.includes('text')) return 'Text';
      if (mimeType.includes('word')) return 'Document';
      return mimeType;
    }

    // Try to determine from filename extension
    const filename = doc.name || doc.filename || '';
    const extension = filename.split('.').pop()?.toUpperCase();
    return extension || 'Unknown';
  }

  /**
   * Format category title
   * @param {string} category - Category key
   * @returns {string} Formatted title
   */
  formatCategoryTitle(category) {
    const titles = {
      travel: 'Travel Documents',
      accommodation: 'Accommodation Documents',
      transportation: 'Transportation Documents',
      insurance: 'Insurance & Legal Documents',
      media: 'Photos & Media Files',
      other: 'Other Documents'
    };

    return titles[category] || 'Documents';
  }

  /**
   * Format upload date
   * @param {Object} doc - Document object
   * @returns {string} Formatted date
   */
  formatUploadDate(doc) {
    const date = doc.uploadDate || doc.createdAt;
    if (!date) return '-';

    try {
      return new Date(date).toLocaleDateString();
    } catch (error) {
      return date;
    }
  }

  /**
   * Format file size
   * @param {Object} doc - Document object
   * @returns {string} Formatted size
   */
  formatFileSize(doc) {
    const size = doc.size || doc.fileSize;
    if (!size || isNaN(size)) return '-';

    const bytes = parseInt(size);
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${Math.round(bytes / (1024 * 1024))} MB`;
  }

  /**
   * Format document description
   * @param {Object} doc - Document object
   * @returns {string} Formatted description
   */
  formatDescription(doc) {
    const description = doc.description || doc.notes || '';
    return description.length > 50 ? description.substring(0, 50) + '...' : description || '-';
  }

  /**
   * Format associations list
   * @param {Array} associations - Associations array
   * @returns {string} Formatted associations
   */
  formatAssociations(associations) {
    if (!Array.isArray(associations) || associations.length === 0) {
      return '-';
    }

    return associations.slice(0, 3).join(', ') + (associations.length > 3 ? '...' : '');
  }

  /**
   * Format tags list
   * @param {Array} tags - Tags array
   * @returns {string} Formatted tags
   */
  formatTags(tags) {
    if (!Array.isArray(tags) || tags.length === 0) {
      return '-';
    }

    return tags.slice(0, 4).join(', ') + (tags.length > 4 ? '...' : '');
  }

  /**
   * Get preview information for this layout
   * @param {*} data - Data to analyze
   * @returns {Object} Preview information
   */
  getPreview(data) {
    const documentsCount = Array.isArray(data) ? data.length : 0;
    const groupedDocs = this.groupDocumentsByType(Array.isArray(data) ? data : []);
    const categoryCount = Object.values(groupedDocs).filter(group => group.length > 0).length;

    return {
      estimatedPages: Math.max(1, Math.ceil(documentsCount / 8) + 1), // +1 for overview
      sections: ['Documents Overview', 'All Documents', 'Documents by Category', 'Document Associations'],
      features: [
        'Organized by document type',
        'Upload dates and file sizes',
        'Document associations',
        'Categorized summaries'
      ],
      dataCount: documentsCount,
      categories: categoryCount
    };
  }
}